/*
  Cart drawer recommendations slider (snippets/cart-recommendations.liquid).
  - Arrow buttons scroll the product cards.
  - When enabled, replaces the cards with recommendations based on the products in the cart,
    fetched as rendered cards from sections/cart-recommendations.liquid.
*/
if (!customElements.get('cart-recommendations')) {
  // The drawer is re-rendered on every cart change, so responses are kept per URL.
  const responses = new Map();

  const fetchCards = (url) => {
    if (!responses.has(url)) {
      responses.set(
        url,
        fetch(url)
          .then((response) => (response.ok ? response.text() : ''))
          .catch(() => {
            responses.delete(url);
            return '';
          })
      );
    }
    return responses.get(url);
  };

  customElements.define(
    'cart-recommendations',
    class CartRecommendations extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('.cart-recs__list');
        this.arrows = this.querySelector('.cart-recs__arrows');
        if (!this.list) return;

        this.querySelectorAll('.cart-recs__arrow').forEach((arrow) => {
          arrow.addEventListener('click', () => this.scrollCards(arrow.dataset.direction));
        });
        this.list.addEventListener('scroll', () => this.updateArrows(), { passive: true });
        this.resizeObserver = new ResizeObserver(() => this.updateArrows());
        this.resizeObserver.observe(this.list);

        this.updateArrows();
        if (this.dataset.recommend === 'true') this.loadRecommendations();
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      scrollCards(direction) {
        const card = this.list.querySelector('.cart-recs__item');
        if (!card) return;
        const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
        const step = card.offsetWidth + gap;
        this.list.scrollBy({ left: direction === 'prev' ? -step : step, behavior: 'smooth' });
      }

      updateArrows() {
        if (!this.arrows) return;
        const maxScroll = this.list.scrollWidth - this.list.clientWidth;
        const position = Math.abs(this.list.scrollLeft);
        this.querySelector('[data-direction="prev"]').disabled = position <= 1;
        this.querySelector('[data-direction="next"]').disabled = position >= maxScroll - 1;
      }

      loadRecommendations() {
        const cartProductIds = (this.dataset.cartProducts || '').split(',').filter(Boolean);
        if (cartProductIds.length === 0) return;

        const limit = parseInt(this.dataset.limit) || 10;
        // Both intents are filled from the Search & Discovery app: "complementary" holds only
        // the products added there by hand, "related" holds the hand-picked related products
        // followed by Shopify's automatic ones. Complementary cards come first.
        const intents =
          this.dataset.source === 'both' ? ['complementary', 'related'] : [this.dataset.source || 'related'];
        const urls = [];
        intents.forEach((intent) => {
          cartProductIds.slice(0, 4).forEach((id) => {
            urls.push(
              `${this.dataset.url}?section_id=cart-recommendations&product_id=${id}&limit=${Math.min(limit, 10)}&intent=${intent}`
            );
          });
        });

        Promise.all(urls.map(fetchCards)).then((responsesHtml) => {
          if (!this.isConnected) return;

          const cardLists = responsesHtml.map((html) => {
            const template = document.createElement('template');
            template.innerHTML = html;
            return Array.from(template.content.querySelectorAll('.cart-recs__item'));
          });

          // Intent by intent, one card from each cart product in turn, skipping products already
          // in the cart.
          const seen = new Set(cartProductIds);
          const cards = [];
          const addCard = (card) => {
            if (!card || seen.has(card.dataset.productId)) return;
            seen.add(card.dataset.productId);
            cards.push(card);
          };
          const perIntent = cartProductIds.slice(0, 4).length;
          for (let start = 0; start < cardLists.length; start += perIntent) {
            const lists = cardLists.slice(start, start + perIntent);
            for (let i = 0; lists.some((list) => list.length > i); i++) {
              lists.forEach((list) => addCard(list[i]));
            }
          }
          // No recommendations: keep the products rendered by Liquid (block settings / fallback).
          if (cards.length === 0) return;

          this.list.replaceChildren(...cards.slice(0, limit));
          this.list.scrollLeft = 0;
          this.hidden = false;
          this.updateArrows();
        });
      }
    }
  );
}
